import React, { useState } from 'react';

/**
 * Contexts
*/
import { useContext } from 'react';
import { AppContext } from '../../contexts/AppContext';

/**
 * MUI Compoments
 */
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ErrorIcon from '@mui/icons-material/Error';
import CircularProgress from '@mui/material/CircularProgress';

/**
 * Hooks
 */
import { useTranslation } from 'react-i18next';
import { useCheckAcknowledge, markPrlDocumentAsAcknowledged } from '../../hooks/PRLDocuments/useCheckAcknowledge';

export default function DocumentCard({ document, workId = null }) {
    const { t } = useTranslation();
    const appData = useContext(AppContext);
    const { ack, loading, error, refetch } = useCheckAcknowledge(appData.token, appData.userInfo?.id, document.id, workId);
    const [acknowledging, setAcknowledging] = useState(false);

    // TODO check internet connection, if user does not have it, show a message

    const handleAcknowledge = async () => {
        setAcknowledging(true);
        try {
            const result = await markPrlDocumentAsAcknowledged(
                appData.token,
                appData.userInfo?.id,
                document.id,
                { wid: document.work_id || workId || null }
            );

            if (result.ok) {
                // Refresh the acknowledge state
                await refetch();
            } else {
                console.error('Failed to acknowledge document');
            }
        } catch (err) {
            console.error('Error acknowledging document:', err);
        } finally {
            setAcknowledging(false);
        }
    };

    const AckStatus = () => {

        if (loading) {
            return (
                <CircularProgress />
            );
        }

        if (error) {
            console.error(error);

            return (
                <div style={{ display: 'flex', alignItems: 'center', color: 'red' }}>
                    <ErrorIcon />
                    <Typography variant="body1">{t('Error')}</Typography>
                </div>
            );
        }

        if (Array.isArray(ack) && ack.length > 0) {
            return (
                <Chip label={t('Acknowledged')} color="success" />
            );
        }

        return (
            <Button
                size="small"
                onClick={handleAcknowledge}
                disabled={acknowledging}
                startIcon={acknowledging ? <CircularProgress size={16} /> : null}
            >
                {acknowledging ? t('Processing...') : t('Acknowledge')}
            </Button>
        );
    };

    const handleDownload = e => {
        e.preventDefault();
        const uid = appData.userInfo?.id || null;
        let finalDownloadURL = document.download_url;
        if (uid) {
            finalDownloadURL += `?uid=${uid}`;
        }
        window.open(finalDownloadURL, '_blank');
        console.log('Downloading', finalDownloadURL);
    };

    return (
        <>
            <Card
                sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    backgroundColor: theme => theme.palette.grey[200]
                }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <CardContent sx={{ flex: '1 0 auto', width: '100%' }}>
                        <Typography component="div" variant="h5">
                            {document.name}
                        </Typography>

                        <Accordion sx={{ width: '100%', flex: 1, marginTop: 2 }} disabled={document.notes ? false : true}>
                            <AccordionSummary
                                expandIcon={<ArrowDownwardIcon />}
                                aria-controls="panel1-content"
                                id="panel1-header"
                            >
                                <Typography>{t('Notes')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography>{document.notes}</Typography>
                            </AccordionDetails>
                        </Accordion>
                        <Accordion sx={{ width: '100%', flex: 1 }}>
                            <AccordionSummary
                                expandIcon={<ArrowDownwardIcon />}
                                aria-controls="panel2-content"
                                id="panel2-header"
                            >
                                <Typography>{t('Details')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography sx={{
                                    display: 'flex',
                                    gap: '20px',
                                    justifyContent: 'space-between'
                                }}>{t('File extension')}
                                    <Chip label={document.extension} sx={{
                                        textTransform: 'uppercase'
                                    }} />
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    </CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pl: 1, pr: 1, pb: 1 }}>
                        <Button size="small" onClick={handleDownload}>
                            {t('Download')}
                        </Button>
                        <AckStatus />
                    </Box>
                </Box>
            </Card>
        </>
    );
}